import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast/headless'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router'
import api from '../../api'
import { AdminContext, CampaignContext, ProjectContext } from '../../contexts'
import { LaunchApproval } from '../../types'
import { Alert, Menu, MenuItem } from '../../ui'
import Button from '../../ui/Button'
import { ArchiveIcon, DuplicateIcon, ForbiddenIcon, RestartIcon, SendIcon } from '../../ui/icons'
import PageContent from '../../ui/PageContent'
import { NavigationTabs } from '../../ui/Tabs'
import { checkProjectRole } from '../../utils'
import { DuplicateCampaignModal, DuplicateCampaignOptions } from './DuplicateCampaignModal'
import LaunchCampaign from './launch/LaunchCampaign'
import { TemplateContextProvider } from './template/TemplateContextProvider'
import { CampaignTag } from './ui/CampaignTag'

function getRequesterName(approval: LaunchApproval): string {
    const { requester } = approval
    if (requester?.first_name && requester?.last_name) {
        return `${requester.first_name} ${requester.last_name}`
    }
    return requester?.email ?? 'Someone'
}

export default function CampaignDetail() {
    const [project] = useContext(ProjectContext)
    const admin = useContext(AdminContext)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [campaign, setCampaign] = useContext(CampaignContext)
    const [isLaunchOpen, setIsLaunchOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [pendingApproval, setPendingApproval] = useState<LaunchApproval | undefined>()
    const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)
    const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)

    const isPublisherOrAdmin = checkProjectRole('publisher', project.role)
    const isRequester = pendingApproval?.requester_id === admin?.id
    const approvalMode = isRequester ? 'review' : 'approve'

    const fetchPendingApproval = async () => {
        if (campaign.state === 'draft') {
            try {
                const result = await api.approvals.search(project.id, {
                    entity_type: 'campaign',
                    entity_id: campaign.id,
                    status: 'pending',
                } as any)
                if (result.results.length > 0) {
                    setPendingApproval(result.results[0])
                } else {
                    setPendingApproval(undefined)
                }
            } catch {
                // Ignore errors - just don't show the alert
            }
        }
    }

    useEffect(() => {
        setPendingApproval(undefined)
        void fetchPendingApproval()
    }, [project.id, campaign.id, campaign.state])

    async function duplicate({ includeVariants, locales, type }: DuplicateCampaignOptions) {
        const duplicated = await api.campaigns.duplicate(project.id, campaign.id, includeVariants, locales, type)
        await navigate(`/projects/${project.id}/campaigns/${duplicated.id}`)
        toast.success(t('campaign_duplicated'))
    }

    async function handleArchive() {
        await api.campaigns.delete(project.id, campaign.id)
        await navigate(`/projects/${project.id}/campaigns`)
    }

    async function handleUnarchive() {
        await api.campaigns.restore(project.id, campaign.id)
        window.location.reload()
    }

    async function handleAbort() {
        if (!confirm(t('campaign_abort_confirm'))) return
        setIsLoading(true)
        const value = await api.campaigns.launch(project.id, campaign.id, { state: 'aborted' })
        setCampaign(value)
        setIsLoading(false)
    }

    const tabs = [
        {
            key: 'details',
            to: '',
            children: t('details'),
        },
        {
            key: 'design',
            to: 'design',
            children: t('design'),
        },
        {
            key: 'delivery',
            to: 'delivery',
            children: t('delivery'),
        },
        {
            key: 'history',
            to: 'history',
            children: t('history'),
        },
    ]

    const renderStateAction = () => {
        const { state, send_at, type, deleted_at } = campaign

        if (type === 'trigger') return null
        if (!checkProjectRole('editor', project.role)) return null

        if (deleted_at) {
            return (
                <Button icon={<RestartIcon />} onClick={handleUnarchive}>
                    {t('campaign_unarchive')}
                </Button>
            )
        }

        // Editors can launch/request launch for draft campaigns or restart aborted campaigns
        if (state === 'draft') {
            return (
                <Button
                    icon={<SendIcon />}
                    onClick={() => pendingApproval ? setIsApprovalModalOpen(true) : setIsLaunchOpen(true)}
                >
                    {t(pendingApproval ? 'approval_review_launch' : 'launch_campaign')}
                </Button>
            )
        }

        if (state === 'aborted') {
            return (
                <Button icon={<RestartIcon />} onClick={() => setIsLaunchOpen(true)}>
                    {t('restart_campaign')}
                </Button>
            )
        }

        // All other state actions require publisher or higher
        if (!isPublisherOrAdmin) return null

        if (state === 'aborting') {
            return (
                <Button icon={send_at ? <SendIcon /> : <ForbiddenIcon />} isLoading={true}>
                    {t(send_at ? 'rescheduling' : 'campaign_abort')}
                </Button>
            )
        }

        if (state === 'loading' || state === 'running') {
            return (
                <Button icon={<ForbiddenIcon />} isLoading={isLoading} onClick={handleAbort}>
                    {t('campaign_abort')}
                </Button>
            )
        }

        if (state === 'scheduled') {
            return (
                <>
                    <Button icon={<SendIcon />} onClick={() => setIsLaunchOpen(true)}>
                        {t('change_schedule')}
                    </Button>
                    <Button icon={<ForbiddenIcon />} isLoading={isLoading} onClick={handleAbort}>
                        {t('campaign_abort')}
                    </Button>
                </>
            )
        }

        return null
    }

    const renderApprovalAlert = () => {
        if (!pendingApproval) return null

        const showAlert = isRequester || isPublisherOrAdmin
        if (!showAlert) return null

        return (
            <Alert
                variant="plain"
                title={t(isRequester ? 'approval_pending_requester_alert_title' : 'approval_pending_alert_title')}
                actions={
                    <Button size="small" onClick={() => setIsApprovalModalOpen(true)}>
                        {t('approval_review')}
                    </Button>
                }
            >
                {isRequester
                    ? t('approval_pending_requester_alert_body')
                    : t('approval_pending_alert_body', { requester: getRequesterName(pendingApproval) })}
            </Alert>
        )
    }

    return (
        <PageContent
            title={campaign.name}
            desc={campaign.state !== 'draft' && (
                <CampaignTag campaign={campaign} />
            )}
            actions={
                <>
                    {renderStateAction()}
                    <Menu size="regular">
                        <MenuItem onClick={() => setIsDuplicateOpen(true)}>
                            <DuplicateIcon />{t('duplicate')}
                        </MenuItem>
                        {isPublisherOrAdmin && <MenuItem onClick={handleArchive}>
                            <ArchiveIcon />{t('archive')}
                        </MenuItem>}
                    </Menu>
                </>
            }
            fullscreen={true}
        >
            {renderApprovalAlert()}
            <NavigationTabs tabs={tabs} />
            <TemplateContextProvider campaign={campaign} setCampaign={setCampaign}>
                <Outlet />
                <LaunchCampaign open={isLaunchOpen} onClose={setIsLaunchOpen} onApprovalComplete={fetchPendingApproval} />
                {pendingApproval && (
                    <LaunchCampaign
                        open={isApprovalModalOpen}
                        onClose={setIsApprovalModalOpen}
                        pendingApproval={pendingApproval}
                        mode={approvalMode}
                        onApprovalComplete={() => setPendingApproval(undefined)}
                    />
                )}
            </TemplateContextProvider>
            <DuplicateCampaignModal
                open={isDuplicateOpen}
                onClose={setIsDuplicateOpen}
                templates={campaign.templates}
                campaignType={campaign.type}
                onDuplicate={duplicate}
            />
        </PageContent>
    )
}
